import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import {
  AssetService,
  MachineGroupInfo,
  MachineInfo,
  MachineSerialsV2Service,
  PlantInfo,
  StoreV2Subject,
  UNIT_MAPPERS,
  UnbalanceUnit,
  UnitSettingsV2Service,
  WithOnDestroy,
  ungroupedId,
} from '@schenck/rotec-components';
import { Observable, ReplaySubject, Subject, combineLatest, interval, of } from 'rxjs';
import {
  catchError,
  filter,
  map,
  shareReplay,
  startWith,
  switchMap,
  take,
  takeUntil,
} from 'rxjs/operators';
import { StatisticsApiRequest } from 'src/api';
import { SettingsApi, TileAssetInfo, TilePageInfo } from 'src/api/settings.api';
import { CustomDateAdapter } from '../datetime-picker/datetime-picker.component';
import {
  DateRange,
  RangeAndInterval,
  intervalDefault,
  quickRangeDefault,
} from '../range-interval-picker/range-interval-picker.model';

/**
 * This services is used by the `TilePageComponent`. Inject it in your service to react to poll events.
 */
@Injectable({ providedIn: 'root' })
export class TilePagesService extends WithOnDestroy {
  private destroy$ = new Subject();
  private settingsLoaded$ = new ReplaySubject<boolean>(0);
  private _state$ = new StoreV2Subject<TilePageInfo>(
    {
      main: {
        selected: undefined,
        range: quickRangeDefault,
      },
      interval: intervalDefault,
    },

    'TilePagesService'
  );
  private assetsLoaded$ = new ReplaySubject<boolean>();

  /**
   * The raw tile page info state. Should only be used internally.
   */
  state$ = this._state$.asObservable();
  /**
   * Returns the current asset name.
   */
  assetName$ = this.settingsLoaded$.pipe(
    switchMap(() => this.state$),
    switchMap((state) => this.getAssetName$(state.main.selected))
  );

  /**
   * Returns the current asset name for comparison or undefined, if nothing selected.
   */
  compareAssetName$ = this.settingsLoaded$.pipe(
    switchMap(() => this.state$),
    switchMap((state) =>
      state.compare ? this.getAssetName$(state.compare.selected) : of(undefined)
    )
  );

  constructor(
    private settingsApi: SettingsApi,
    private dateAdapter: CustomDateAdapter,
    private assetService: AssetService,
    private machineSerialsService: MachineSerialsV2Service,
    private translate: TranslateService,
    private router: Router,
    private settings: UnitSettingsV2Service
  ) {
    super();
    this.loadSettings();
    this.loadAssets();
  }

  /**
   * Passed in callback function gets invoked everytime a refresh should happen and
   * the backend should be called again.
   * Will only poll on given page
   * Use this to make an api request.
   */
  onPoll$<T>(
    pageUrl: string,
    withRotorType: boolean,
    withUnbalUnit: boolean,
    doCall$: (def: StatisticsApiRequest) => Observable<T>
  ) {
    return combineLatest([this.settings.state$, this.settingsLoaded$, this.assetsLoaded$]).pipe(
      filter(([state, loaded, assetsLoaded]) => {
        return !!state && loaded && assetsLoaded;
      }),
      switchMap(() => this.state$),
      switchMap((state) =>
        interval(state.interval * 1000).pipe(
          startWith(0),
          filter(() => !!state.main.selected),
          switchMap(() => {
            const request$ = this.createApiRequest$(state, withRotorType, withUnbalUnit);
            const shouldPoll = this.router.url.endsWith(pageUrl);
            if (shouldPoll) {
              return request$;
            }
            // Wait until we are on the given page again and then start polling right away.
            return this.router.events.pipe(
              filter((event: any) => event instanceof NavigationEnd && event.url === pageUrl),
              switchMap(() => request$)
            );
          })
        )
      ),
      takeUntil(this.onDestroy$),
      switchMap((request) => doCall$(request)),
      // Store results for replay to avoid unnecessary requests
      // when switching between pages.
      shareReplay(1)
    );
  }

  /**
   * Sets selected asset id.
   */
  setSelected(selected: string) {
    this._state$.update((state) => ({ ...state, main: { ...state.main, selected } }));
  }

  /**
   * Sets select compare asset id.
   */
  setCompare(compare: TileAssetInfo | undefined) {
    this._state$.update((state) => ({ ...state, compare }));
  }

  /**
   * Sets date range and refresh interval.
   */
  setMainRangeAndInterval(rangeAndInterval: RangeAndInterval) {
    this._state$.update((state) => ({
      ...state,
      interval: rangeAndInterval.interval,
      main: { ...state.main, range: rangeAndInterval.range },
    }));
  }

  /**
   * Sets the rotor type.
   */
  setRotorType(rotorType: string) {
    this._state$.patch({ rotorType });
  }

  /**
   * Sets the unbalance unit.
   */
  setUnbalUnit(unbalUnit: UnbalanceUnit) {
    this._state$.patch({ unbalUnit });
  }

  getAssetNamesOnce$() {
    return combineLatest([this.assetName$, this.compareAssetName$]).pipe(
      map(
        ([assetName, compareAssetName]) =>
          <[string, string?, string?]>[assetName, compareAssetName, this._state$.value.rotorType]
      ),
      take(1)
    );
  }

  private createApiRequest$(
    state: TilePageInfo,
    withRotorType: boolean,
    withUnbalUnit: boolean
  ): Observable<StatisticsApiRequest> {
    const machineSerials$ = this.machineSerialsService.getMachineSerialsFromId$(
      state.main.selected
    );
    const compareMachineSerials$ = this.machineSerialsService.getMachineSerialsFromId$(
      state.compare && state.compare.selected
    );
    return combineLatest([machineSerials$, compareMachineSerials$]).pipe(
      filter(
        ([machineSerials, compareMachineSerials]) =>
          !!machineSerials?.length || !!compareMachineSerials?.length
      ),
      map(([machineSerials, compareMachineSerials]) => {
        const createApiReq = (serials: string[], tileAssetInfo: TileAssetInfo | undefined) => {
          return tileAssetInfo
            ? {
                from: this.dateAdapter.deserialize(tileAssetInfo.range.from)!,
                to: this.dateAdapter.deserialize(tileAssetInfo.range.to)!,
                machineSerials: serials,
              }
            : undefined;
        };
        const unbalanceValueKey =
          Object.keys(UNIT_MAPPERS)[
            Object.values(UNIT_MAPPERS).indexOf(this.settings.state.units.unbalanceValue)
          ];

        return {
          main: createApiReq(machineSerials, state.main)!,
          compare: createApiReq(compareMachineSerials, state.compare),
          rotorType: withRotorType ? state.rotorType : undefined,
          unbalUnit: withUnbalUnit ? (unbalanceValueKey as UnbalanceUnit) : undefined,
        };
      })
    );
  }

  private getAssetName$(id: string | undefined): Observable<string> {
    if (!id) {
      return of('');
    }
    if (id === ungroupedId) {
      return this.translate.get('MC_Ungrouped');
    }
    return combineLatest([
      this.assetService.plantRecord$,
      this.assetService.machineRecord$,
      this.assetService.machineGroupsRecord$,
    ]).pipe(
      map(([plants, machines, groups]) => {
        const asset: PlantInfo | MachineInfo | MachineGroupInfo =
          plants[id] || machines[id] || groups[id];

        const assetMachineSerials = this.machineSerialsService.getMachineSerialsFromId(asset.id);

        return !!assetMachineSerials?.length ? asset.name : null;
      }),
      catchError(() => of(''))
    );
  }

  private loadSettings() {
    this.settingsApi.loadTilePageSettings$().subscribe((tilePageInfo: TilePageInfo | null) => {
      const _tilePageInfo = tilePageInfo
        ? {
            main: {
              selected: tilePageInfo.main.selected ? tilePageInfo.main.selected : undefined,
              range: tilePageInfo.main.range ? tilePageInfo.main.range : quickRangeDefault,
            },
            interval: tilePageInfo.interval ? tilePageInfo.interval : intervalDefault,
          }
        : {
            main: {
              selected: undefined,
              range: quickRangeDefault,
            },
            interval: intervalDefault,
          };

      this._state$.next(this.deserialize(_tilePageInfo));
      this.settingsLoaded$.next(true);
      this.saveChangesToSettings();
    });
  }

  private loadAssets(): void {
    combineLatest([
      this.assetService.plants$,
      this.assetService.machines$,
      this.assetService.machineGroups$,
    ])
      .pipe(
        filter((assets) => assets.every((asset) => !!asset)),
        take(1)
      )
      .subscribe(() => this.assetsLoaded$.next(true));
  }

  private saveChangesToSettings() {
    this._state$.subscribe((state) => this.settingsApi.setTilePageSettings$(state));
  }

  private deserialize(tilePageInfo: TilePageInfo): TilePageInfo {
    // Dates are stringified, try to parse them if they are ISO strings
    tilePageInfo.main.range = instantiateDateRange(tilePageInfo.main.range);
    if (tilePageInfo.compare) {
      tilePageInfo.compare.range = instantiateDateRange(tilePageInfo.compare.range);
    }
    return tilePageInfo;

    function instantiateDateRange(dateRange: DateRange): DateRange {
      return {
        from: instantiateDate(dateRange.from),
        to: instantiateDate(dateRange.to),
      };
    }

    function instantiateDate(dateStr: string | Date | null) {
      if (!dateStr) {
        return <any>null;
      }
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) {
        return dateStr;
      }
      return date;
    }
  }
}
